import moment from "moment";
import React, { useMemo, useState } from "react";

// Vitals over time, as the clinical Observation Results shows them: one tile
// per vital with the latest reading, a line for weight, height, BMI, oxygen
// saturation, heart rate and temperature, and a DUMBBELL for blood pressure
// (a bar from the diastolic to the systolic reading at each date, a dot at each
// end, against the usual range).
//
// Readings come from two places, merged: the clinic's APPROVED results (lines
// named Weight, Blood pressure (systolic) ...) and the patient's own entries.

const VITAL_NAMES = [
  ["weight", /^\s*(body\s*)?weight\b/i],
  ["height", /^\s*(body\s*)?height\b/i],
  ["systolic", /systolic/i],
  ["diastolic", /diastolic/i],
  ["oxygen", /oxygen|spo2|sp\s*o2/i],
  ["heartRate", /heart\s*rate|pulse/i],
  ["temperature", /temperature/i],
  ["bmi", /\bbmi\b|body mass/i],
];

export function vitalKeyOf(text) {
  const t = String(text || "");
  const hit = VITAL_NAMES.find(([, re]) => re.test(t));
  return hit ? hit[0] : null;
}

// A result line that is a vital sign, so it is drawn here and not among the labs.
export const isVitalLine = (line) => vitalKeyOf(line?.text || line?.identifier) !== null;

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const SPECS = {
  weight: { label: "Weight", unit: "kg", band: null, decimals: 1 },
  height: { label: "Height", unit: "m", band: null, decimals: 2 },
  bmi: { label: "BMI (calculated)", unit: "kg/m²", band: [18.5, 24.9], decimals: 1 },
  oxygen: { label: "Oxygen saturation", unit: "%", band: [95, 100], decimals: 0 },
  heartRate: { label: "Heart rate", unit: "bpm", band: [60, 100], decimals: 0 },
  temperature: { label: "Temperature", unit: "°C", band: [36.1, 37.8], decimals: 1 },
};

// Points per vital, oldest first: { ts, value, mine }.
export function buildVitalSeries({ lines = [], entries = [], clinic = {} }) {
  const raw = { weight: [], height: [], systolic: [], diastolic: [], oxygen: [], heartRate: [], temperature: [] };
  lines.forEach((l) => {
    const key = vitalKeyOf(l.text || l.identifier);
    const v = num(l.observationValue);
    const when = moment(l.dateTimeofTransaction);
    if (!key || key === "bmi" || v === null || !when.isValid() || !raw[key]) return;
    raw[key].push({ ts: when.valueOf(), value: v, mine: false });
  });
  entries.forEach((e) => {
    const key = { bpSystolic: "systolic", bpDiastolic: "diastolic" }[e.type] || e.type;
    const v = num(e.value);
    if (raw[key] && v !== null) raw[key].push({ ts: new Date(e.date).getTime(), value: v, mine: true });
  });
  // The questionnaire's current figure stands in when nothing else was recorded.
  const now = Date.now();
  if (!raw.weight.length && num(clinic.weight)) raw.weight.push({ ts: now, value: num(clinic.weight), mine: false, fromRecord: true });
  if (!raw.height.length && num(clinic.height)) raw.height.push({ ts: now, value: num(clinic.height), mine: false, fromRecord: true });
  if (!raw.systolic.length && num(clinic.systolic) && num(clinic.diastolic)) {
    raw.systolic.push({ ts: now, value: num(clinic.systolic), mine: false, fromRecord: true });
    raw.diastolic.push({ ts: now, value: num(clinic.diastolic), mine: false, fromRecord: true });
  }
  Object.values(raw).forEach((a) => a.sort((x, y) => x.ts - y.ts));

  // BMI at each weight, using the height nearest in time.
  const heightAt = (ts) => {
    if (!raw.height.length) return null;
    return raw.height.reduce((best, p) => (Math.abs(p.ts - ts) < Math.abs(best.ts - ts) ? p : best)).value;
  };
  const bmi = raw.weight
    .map((w) => {
      const h = heightAt(w.ts);
      return h > 0 ? { ts: w.ts, value: w.value / (h * h), mine: w.mine } : null;
    })
    .filter(Boolean);

  // Blood pressure: pair the two readings taken within the same hour.
  const bp = [];
  raw.systolic.forEach((s) => {
    const d = raw.diastolic.find((x) => Math.abs(x.ts - s.ts) < 3600 * 1000);
    if (d) bp.push({ ts: s.ts, sys: s.value, dia: d.value, mine: s.mine, fromRecord: s.fromRecord });
  });

  return { weight: raw.weight, height: raw.height, bmi, bp, oxygen: raw.oxygen, heartRate: raw.heartRate, temperature: raw.temperature };
}

const W = 280;
const H = 150;
const PL = 36;
const PR = 12;
const PT = 14;
const PB = 28;
const IW = W - PL - PR;
const IH = H - PT - PB;

function scale(values, band) {
  let lo = Math.min(...values, ...(band ? [band[0]] : []));
  let hi = Math.max(...values, ...(band ? [band[1]] : []));
  if (lo === hi) { lo -= 1; hi += 1; }
  const pad = (hi - lo) * 0.18;
  return [lo - pad, hi + pad];
}

const shortDate = (ts) => moment(ts).format("DD MMM YY");

function Axes({ lo, hi, pts, xOf, decimals }) {
  const ticks = [lo, (lo + hi) / 2, hi];
  const yOf = (v) => PT + IH - ((v - lo) / (hi - lo)) * IH;
  const first = pts[0];
  const last = pts[pts.length - 1];
  return (
    <>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={PL} x2={W - PR} y1={yOf(t)} y2={yOf(t)} stroke="#e3e8ee" strokeWidth="1" />
          <text x={PL - 5} y={yOf(t) + 3} textAnchor="end" fontSize="9" fill="#5f6f80">{t.toFixed(decimals)}</text>
        </g>
      ))}
      <text x={xOf(0)} y={H - 10} textAnchor={pts.length > 1 ? "start" : "middle"} fontSize="9" fill="#5f6f80">{shortDate(first.ts)}</text>
      {pts.length > 1 && <text x={xOf(pts.length - 1)} y={H - 10} textAnchor="end" fontSize="9" fill="#5f6f80">{shortDate(last.ts)}</text>}
    </>
  );
}

function LineTile({ spec, points, color }) {
  const [hover, setHover] = useState(null);
  const n = points.length;
  const [lo, hi] = scale(points.map((p) => p.value), spec.band);
  const xOf = (i) => (n <= 1 ? PL + IW / 2 : PL + (i / (n - 1)) * IW);
  const yOf = (v) => PT + IH - ((v - lo) / (hi - lo)) * IH;
  const path = points.map((p, i) => `${i ? "L" : "M"} ${xOf(i)} ${yOf(p.value)}`).join(" ");
  const last = points[n - 1];
  const shown = hover !== null ? points[hover] : last;
  return (
    <div className="ox-vg">
      <div className="ox-vg__head">
        <span>{spec.label}</span>
        <strong>{shown.value.toFixed(spec.decimals)} <small>{spec.unit}</small></strong>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${spec.label}: ${n} reading${n === 1 ? "" : "s"}, latest ${last.value.toFixed(spec.decimals)} ${spec.unit} on ${shortDate(last.ts)}`}>
        {spec.band && (
          <rect x={PL} width={IW} y={yOf(spec.band[1])} height={Math.max(0, yOf(spec.band[0]) - yOf(spec.band[1]))} fill="#2f7f72" opacity="0.1" />
        )}
        <Axes lo={lo} hi={hi} pts={points} xOf={xOf} decimals={spec.decimals} />
        {n > 1 && <path d={path} fill="none" stroke={color} strokeWidth="2" />}
        {points.map((p, i) => (
          <circle key={p.ts + "-" + i} cx={xOf(i)} cy={yOf(p.value)} r={hover === i ? 5 : 3.5} fill={p.mine ? "#fff" : color} stroke={color} strokeWidth="2"
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <title>{`${p.value.toFixed(spec.decimals)} ${spec.unit} · ${shortDate(p.ts)}${p.mine ? " · entered by you" : ""}`}</title>
          </circle>
        ))}
      </svg>
      <div className="ox-vg__foot">{shown.mine ? "Entered by you · " : ""}{moment(shown.ts).format("DD MMM YYYY")}</div>
    </div>
  );
}

// Blood pressure: a bar from diastolic to systolic at each date, a dot at each
// end. The shaded bands are the usual ranges for each.
function DumbbellTile({ points }) {
  const [hover, setHover] = useState(null);
  const n = points.length;
  const [lo, hi] = scale([...points.map((p) => p.sys), ...points.map((p) => p.dia), 60, 120], null);
  const xOf = (i) => (n <= 1 ? PL + IW / 2 : PL + (i / (n - 1)) * IW);
  const yOf = (v) => PT + IH - ((v - lo) / (hi - lo)) * IH;
  const last = points[n - 1];
  const shown = hover !== null ? points[hover] : last;
  return (
    <div className="ox-vg">
      <div className="ox-vg__head">
        <span>Blood pressure</span>
        <strong>{shown.sys}/{shown.dia} <small>mmHg</small></strong>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Blood pressure: ${n} reading${n === 1 ? "" : "s"}, latest ${last.sys} over ${last.dia} mmHg on ${shortDate(last.ts)}`}>
        <rect x={PL} width={IW} y={yOf(120)} height={Math.max(0, yOf(90) - yOf(120))} fill="#2f7f72" opacity="0.1" />
        <rect x={PL} width={IW} y={yOf(80)} height={Math.max(0, yOf(60) - yOf(80))} fill="#3d6b9e" opacity="0.1" />
        <Axes lo={lo} hi={hi} pts={points} xOf={xOf} decimals={0} />
        {points.map((p, i) => {
          const x = xOf(i);
          const high = p.sys >= 140 || p.dia >= 90;
          const color = high ? "#b3261e" : "#2f7f72";
          return (
            <g key={p.ts + "-" + i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <line x1={x} x2={x} y1={yOf(p.sys)} y2={yOf(p.dia)} stroke={color} strokeWidth="3" strokeLinecap="round" opacity="0.55" />
              <circle cx={x} cy={yOf(p.sys)} r="4.5" fill={p.mine ? "#fff" : color} stroke={color} strokeWidth="2" />
              <circle cx={x} cy={yOf(p.dia)} r="4.5" fill={p.mine ? "#fff" : "#3d6b9e"} stroke="#3d6b9e" strokeWidth="2" />
              <title>{`${p.sys}/${p.dia} mmHg · ${shortDate(p.ts)}${p.mine ? " · entered by you" : ""}`}</title>
            </g>
          );
        })}
      </svg>
      <div className="ox-vg__foot">
        <span className="ox-vg__key"><i style={{ background: "#2f7f72" }} />Systolic</span>
        <span className="ox-vg__key"><i style={{ background: "#3d6b9e" }} />Diastolic</span>
        {shown.mine ? "Entered by you · " : ""}{moment(shown.ts).format("DD MMM YYYY")}
      </div>
    </div>
  );
}

const COLORS = { weight: "#2a78d6", height: "#4a3aa7", bmi: "#eb6834", oxygen: "#1baf7a", heartRate: "#e34948", temperature: "#eda100" };

export default function VitalsGraphs({ lines, entries, clinic }) {
  const series = useMemo(() => buildVitalSeries({ lines, entries, clinic }), [lines, entries, clinic]);
  const any = series.weight.length || series.height.length || series.bp.length || series.oxygen.length || series.heartRate.length || series.temperature.length;
  if (!any) return null;
  return (
    <section className="ox-vitals ox-vgraphs" aria-label="Vitals over time">
      <header className="ox-vitals__head"><h3>Vitals over time</h3><span className="ox-hint">Hollow dots are readings you entered</span></header>
      <div className="ox-vg__grid">
        {series.weight.length > 0 && <LineTile spec={SPECS.weight} points={series.weight} color={COLORS.weight} />}
        {series.height.length > 0 && <LineTile spec={SPECS.height} points={series.height} color={COLORS.height} />}
        {series.bmi.length > 0 && <LineTile spec={SPECS.bmi} points={series.bmi} color={COLORS.bmi} />}
        {series.bp.length > 0 && <DumbbellTile points={series.bp} />}
        {series.oxygen.length > 0 && <LineTile spec={SPECS.oxygen} points={series.oxygen} color={COLORS.oxygen} />}
        {series.heartRate.length > 0 && <LineTile spec={SPECS.heartRate} points={series.heartRate} color={COLORS.heartRate} />}
        {series.temperature.length > 0 && <LineTile spec={SPECS.temperature} points={series.temperature} color={COLORS.temperature} />}
      </div>
    </section>
  );
}
